import {configureStore, ThunkAction, Action, combineReducers} from "@reduxjs/toolkit";
import {undoableLabelEdit, ReferenceDataReducer} from "../components/MainReducer";
import persistReducer from "redux-persist/es/persistReducer";
import persistStore from "redux-persist/es/persistStore";
import localforage from "localforage";
import {AuthReducer} from "../components/Auth/AuthReducer";
import CompanyDataReducer from "../components/CompanyDataTables/CompanyDataReducer";
import {createTransform} from "redux-persist";

const cleanBlobUrlsTransform = createTransform(
  (inboundState: any) => {
    if (!inboundState || !inboundState.labelItems) return inboundState;
    
    return {
      ...inboundState,
      labelItems: inboundState.labelItems.map((item: any) => {
        if (item.value && typeof item.value === 'string' && item.value.startsWith('blob:')) {
          return { ...item, value: '' };
        }
        return item;
      }),
    };
  },
  (outboundState: any) => outboundState,
  { whitelist: ['present'] }
);

const labelEditPersistConfig = {
  key: "labelEdit",
  storage: localforage,
  debounce: 300,
  transforms: [cleanBlobUrlsTransform],
};

const refDataPersistConfig = {
  key: "refData",
  storage: localforage,
  blacklist: ["barcodeTags", "imgTags", "textTags", "techTags", "dpiValues", "labelSizes", "allTags", "employees"], // Don't persist lookup data that's loaded from server
  debounce: 300,
};

const authPersistConfig = {
  key: "auth",
  storage: localforage,
  whitelist: ["companyId", "companyName", "role"],
};

const companyPersistConfig = {
  key: "companyData",
  storage: localforage,
  whitelist: [],
  debounce: 300,
}

const rootReducer = combineReducers({
  auth: persistReducer(authPersistConfig, AuthReducer),
  labelEdit: persistReducer(labelEditPersistConfig, undoableLabelEdit),
  refData: persistReducer(refDataPersistConfig, ReferenceDataReducer),
  companyData: persistReducer(companyPersistConfig, CompanyDataReducer),
})

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      immutableCheck: false,
      serializableCheck: false,
    }),
  devTools: process.env.NODE_ENV !== 'production' ? {
    maxAge: 50,
    actionSanitizer: (action: any) => ({
      ...action,
      payload: action.payload && typeof action.payload === 'object' 
        ? '<<SANITIZED>>'
        : action.payload,
    }),
    stateSanitizer: (state: any) => ({
      ...state,
      labelEdit: state.labelEdit ? {
        past: `<<${state.labelEdit.past?.length || 0} past states>>`,
        present: state.labelEdit.present ? {
          ...state.labelEdit.present,
          labelItems: `<<${state.labelEdit.present.labelItems?.length || 0} items>>`,
        } : state.labelEdit.present,
        future: `<<${state.labelEdit.future?.length || 0} future states>>`,
      } : state.labelEdit,
      refData: state.refData ? {
        ...state.refData,
        allTags: `<<${state.refData.allTags?.length || 0} tags>>`,
        templates: `<<${state.refData.templates?.length || 0} templates>>`,
      } : state.refData,
    }),
  } : false,
});
export const persistor = persistStore(store);

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
export type AppThunk<ReturnType = void> = ThunkAction<ReturnType, RootState, unknown, Action<string>>;
